import { Injectable } from '@angular/core';
import { CustomReportsService as ApiCustomReportsService } from '../../api/services/custom-reports.service';
import { CustomReportModel } from '../../api/models/custom-report-model';
import { string2ArrayBuffer } from '../../core/utilities/helper-functions';
import * as _ from 'lodash';
import { AuthService } from '../../core/security/auth.service';
import { ModulesEnum } from '../../core/security/modules.enum';
import { UnsubscribeReportRequest } from '../../api/models/unsubscribe-report-request';
import { lastValueFrom } from 'rxjs';

export const ReportTypes = {
  agentGroups: 'agentGroups',
  huntGroups: 'huntGroups',
  usersSummary: 'usersSummary',
  domainsSummary: 'domainsSummary',
  agentGroupsV2: 'agentGroupsV2',
  huntGroupsV2: 'huntGroupsV2',
  usersSummaryV2: 'usersSummaryV2',
  callLogsV2: 'callLogsV2',
  agentsLoginLogoutV2: 'agentsLoginLogoutV2',
}

@Injectable()
export class CustomReportsService {
  domainsLookup = [];

  dataSourcesBasic = [
    {
      id: ReportTypes.agentGroups,
      text: 'Agent Groups Report (legacy)',
    },
    {
      id: ReportTypes.huntGroups,
      text: 'Hunt Groups Report (legacy)',
    },
    {
      id: ReportTypes.usersSummary,
      text: 'Agents Report (legacy)',
    },
    {
      id: ReportTypes.domainsSummary,
      text: 'Domains Report (legacy)',
    },
  ];

  dataSourcesV2 = [
    {
      id: ReportTypes.agentGroupsV2,
      text: 'Agent Groups Report',
    },
    {
      id: ReportTypes.huntGroupsV2,
      text: 'Hunt Groups Report',
    },
    {
      id: ReportTypes.usersSummaryV2,
      text: 'Agents Report',
    },
    {
      id: ReportTypes.callLogsV2,
      text: 'Call Logs Report',
    },
    {
      id: ReportTypes.agentsLoginLogoutV2,
      text: 'Agent Groups Login / Logout Report',
    },
  ];

  dataSources = [];
  dataSourcesMap = {};
  allDataSourcesMap = {};

  constructor(
    private authService: AuthService,
    private customReportsService: ApiCustomReportsService,
  ) {
    authService.currentUser.subscribe(user => {
      this.dataSources = [];
      this.dataSourcesMap = {};
      this.allDataSourcesMap = {};

      this.dataSourcesBasic.forEach(source => {
        this.allDataSourcesMap[source.id] = source.text;
      });

      this.dataSourcesV2.forEach(source => {
        this.allDataSourcesMap[source.id] = source.text;
      });

      if (user) {
        if (authService.userHasPackage([ModulesEnum.UsersSummaryModule])) {
          this.dataSources = [...this.dataSourcesBasic];
        }
        if (authService.userHasPackage([ModulesEnum.UsersSummaryV2Module])) {
          this.dataSources.push(...this.dataSourcesV2);
        }

        this.dataSources.sort((a, b) => (a.text > b.text ? 1 : b.text > a.text ? -1 : 0));

        this.dataSources.forEach(source => {
          this.dataSourcesMap[source.id] = source.text;
        });
      } else {
        this.dataSourcesMap = {};
      }
    });

    this.getDomainsLookup().then();
  }

  async getCustomReportById(id: string): Promise<CustomReportModel> {
    return await lastValueFrom(
      this.customReportsService.customReportsGetById({
        id: id,
      }),
    );
  }

  async saveCustomReport(model: CustomReportModel): Promise<boolean> {
    await lastValueFrom(
      this.customReportsService.customReportsSave({
        body: model,
      }),
    );

    return true;
  }

  async deleteCustomReportById(id: string): Promise<boolean> {
    await lastValueFrom(
      this.customReportsService.customReportsDeleteById({
        id,
      }),
    );

    return true;
  }

  async downloadCustomReport(id: string, reportName: string): Promise<void> {
    const response = await this.customReportsService.customReportsDownloadById({ id: id }).toPromise();

    const blob = new Blob([string2ArrayBuffer(atob(response))], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);

    reportName = reportName?.replace(new RegExp(' ', 'g'), '-');

    const a = document.createElement('a');
    a.style.display = 'none';
    document.body.appendChild(a);

    a.href = url;
    a.download = `${reportName || 'report'}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);

    return;
  }

  async previewCustomReport(id: string): Promise<void> {
    await lastValueFrom(this.customReportsService.customReportsPreviewById({ id: id }));
  }

  async unsubscribeCustomReport(request: UnsubscribeReportRequest): Promise<void> {
    await lastValueFrom(this.customReportsService.customReportsUnsubscribe({ body: request }));
  }

  async getDomainsLookup(): Promise<void> {
    const response = await lastValueFrom(this.customReportsService.customReportsCustomerDomains());

    if (response) {
      this.domainsLookup = response.map(x => {
        return {
          text: x.alias || x.domain,
          value: `${x.domain}|${x.alias}`,
          id: _.uniqueId('id_'),
        };
      });
    } else {
      this.domainsLookup = [];
    }

    return;
  }
}
